import { useEffect } from "react";

// Full meeting name tooltip (RON-5953): placement and visibility.
//
// Extracted from WeeklyCalendarUI so the create/edit meeting modal can reuse it
// rather than carry a second copy of the geometry below. Both callers render the
// same markup inside their FullCalendar events:
//
//   <div class="fc-booking-title">
//     <div class="fc-booking-title-text"><span>{name}</span></div>
//     <span class="meeting-name-tooltip">{name}</span>
//   </div>
//
// What the reader sees is a SINGLE element on document.body, not the span inside
// each event. The span stays put as the text source and is never displayed. Two
// reasons:
//
// 1. A tooltip inside the event is trapped twice over - clipped by FullCalendar's
//    time-grid scroller, and painted under the all-day section because the event
//    harness is a stacking context of its own. `position: fixed` fixes the first
//    and cannot fix the second. See the rule in main-weekcalendar.css.
// 2. Relocating the event's own span to the body would hand a React-owned node to
//    someone else. FullCalendar re-renders event content freely, and React would
//    later update or unmount a node that is no longer where it left it.
//
// ONE delegated listener on the grid, not a handler per event: a week can hold
// many events, and per-event React state would re-render the whole grid on every
// hover. Nothing here touches state - it writes to the DOM directly, and measures
// once per hover rather than once per render.
//
// The week grid renders the markup above so the visible text can be clipped
// independently of the tooltip's text source. The create/edit modal does NOT:
// its event layout (the clock icon, the time/title ordering) is CSS keyed to
// FullCalendar's own `.fc-event-time` / `.fc-event-title` DOM, so replacing its
// event content would break the layout. It therefore points `titleSelector` at
// `.fc-event-title` and the full text is read from that element itself.
//
// @param rootEl        Element the hover is delegated from (the calendar
//                      wrapper). Null/undefined is a no-op, so callers can pass
//                      a ref value not populated on the first render.
// @param getBoundsEl   Optional () => Element. Returns the element whose top edge
//                      the tooltip must never rise above. The week grid passes
//                      its own time scroller (above it sits the ALL DAY row,
//                      which a tooltip must not cover); the modal passes its
//                      scroll wrapper, since its calendar is `height="auto"` and
//                      has no internal scroller. Falls back to the window edge.
// @param titleSelector Optional CSS selector for the hover target. Defaults to
//                      `.fc-booking-title`.
// @param aboveModal    Optional. The tooltip sits on document.body at a z-index
//                      deliberately BELOW Bootstrap's modal layer, so a stray
//                      tooltip can never cover a booking popup. A caller whose own
//                      calendar lives INSIDE such a modal is the one case where that
//                      rule works against itself - the tooltip is positioned
//                      correctly and painted behind the very popup that owns the
//                      grid it describes. Pass true to raise THIS instance above the
//                      modal layer; off by default, so the week grid keeps the safer
//                      one. See the `.is-above-modal` rule in main-weekcalendar.css.
export default function useMeetingNameTooltip(
    rootEl,
    { getBoundsEl, titleSelector = ".fc-booking-title", aboveModal = false } = {}
) {
    useEffect(() => {
        if (!rootEl) return undefined;

        // Clearance between the event and the tooltip, and the minimum gap it keeps from
        // the edges of the window.
        const TOOLTIP_GAP_PX = 8;
        const VIEWPORT_MARGIN_PX = 8;
        // How far the tail's centre must stay from the bubble's left and right ends.
        // The bubble's corner radius is 8px and the tail is 7px either side of its
        // centre, so anything under 15 would let the tail poke out of a curve.
        const TAIL_EDGE_INSET_PX = 15;

        const tooltip = document.createElement("div");
        tooltip.className = aboveModal
            ? "meeting-name-tooltip-floating is-above-modal"
            : "meeting-name-tooltip-floating";
        document.body.appendChild(tooltip);

        let activeTitle = null;

        const hideTooltip = () => {
            if (!activeTitle) return;

            tooltip.classList.remove("is-visible");
            activeTitle = null;
        };

        const showTooltip = (titleEl) => {
            // Prefer the dedicated source span (week grid); fall back to the element's
            // own text for callers that keep FullCalendar's default event DOM.
            const text = (
                titleEl.querySelector(".meeting-name-tooltip")?.textContent
                ?? titleEl.textContent
            )?.trim();

            if (!text) return;

            tooltip.textContent = text;
            // Visible BEFORE measuring: a `display: none` element has no box, so its
            // height would read as 0 and the flip below could never trigger.
            tooltip.classList.add("is-visible");
            activeTitle = titleEl;

            const anchor = titleEl.getBoundingClientRect();
            const box = tooltip.getBoundingClientRect();

            // Never rise above the caller's own scrollport. Falls back to the window
            // edge when no bounds element is given or it cannot be found.
            const boundsEl = getBoundsEl
                ? getBoundsEl()
                : rootEl.querySelector(".fc-scroller-liquid-absolute");
            const upperBound = Math.max(
                VIEWPORT_MARGIN_PX,
                boundsEl ? boundsEl.getBoundingClientRect().top : VIEWPORT_MARGIN_PX
            );

            // Above by preference, which is where the design puts it; below when there is
            // no room above it - the topmost-row case this whole thing exists for.
            const topIfAbove = anchor.top - box.height - TOOLTIP_GAP_PX;
            const placedAbove = topIfAbove >= upperBound;
            const top = placedAbove ? topIfAbove : anchor.bottom + TOOLTIP_GAP_PX;

            // Keep it on screen horizontally: a 240px tooltip on the last day column would
            // otherwise run off the right edge. Math.max on the upper bound guards the
            // degenerate case of a window narrower than the tooltip itself.
            const maxLeft = window.innerWidth - box.width - VIEWPORT_MARGIN_PX;
            const left = Math.min(
                Math.max(anchor.left, VIEWPORT_MARGIN_PX),
                Math.max(VIEWPORT_MARGIN_PX, maxLeft)
            );

            // The tail sits on whichever edge faces the event.
            tooltip.classList.toggle("is-above", placedAbove);
            tooltip.classList.toggle("is-below", !placedAbove);

            // Where the event's centre falls INSIDE the bubble. This is the whole trick:
            // the bubble's own left edge moves (it is clamped away from the window edge
            // on the last day column), so a tail fixed at 50% would drift off the event.
            // Expressing it relative to the bubble keeps the tail on the event wherever
            // the bubble ended up.
            const anchorCentreX = anchor.left + anchor.width / 2;
            const tailCentre = anchorCentreX - left;

            // Outside the bubble entirely - only reachable on a window narrower than the
            // tooltip, or an extreme corner cell. A tail pointing at empty space reads as
            // broken, so there simply is not one.
            const hasTail = tailCentre >= 0 && tailCentre <= box.width;

            tooltip.classList.toggle("has-tail", hasTail);

            if (hasTail) {
                const maxTail = Math.max(TAIL_EDGE_INSET_PX, box.width - TAIL_EDGE_INSET_PX);
                const tailLeft = Math.min(Math.max(tailCentre, TAIL_EDGE_INSET_PX), maxTail);

                tooltip.style.setProperty("--arrow-left", `${Math.round(tailLeft)}px`);
            }

            tooltip.style.left = `${Math.round(left)}px`;
            tooltip.style.top = `${Math.round(top)}px`;
        };

        const handleMouseOver = (event) => {
            const titleEl = event.target?.closest?.(titleSelector);

            if (!titleEl) return;
            // Moving within the title that is already showing - nothing to redo.
            if (titleEl === activeTitle) return;

            showTooltip(titleEl);
        };

        const handleMouseOut = (event) => {
            const titleEl = event.target?.closest?.(titleSelector);

            if (!titleEl) return;
            // A move to a child of the same title is not a leave. The tooltip is
            // `pointer-events: none`, so it never becomes the related target itself.
            if (event.relatedTarget && titleEl.contains(event.relatedTarget)) return;

            hideTooltip();
        };

        // Fixed coordinates go stale the moment anything moves, and the pointer can sit
        // still while the grid scrolls under it (wheel over an event), so a leave event
        // may never arrive. Dropping the tooltip is cheaper and less jarring than tracking
        // it. Capture phase, because the time grid's own scroll does not bubble.
        rootEl.addEventListener("mouseover", handleMouseOver);
        rootEl.addEventListener("mouseout", handleMouseOut);
        window.addEventListener("scroll", hideTooltip, true);
        window.addEventListener("resize", hideTooltip);

        return () => {
            rootEl.removeEventListener("mouseover", handleMouseOver);
            rootEl.removeEventListener("mouseout", handleMouseOut);
            window.removeEventListener("scroll", hideTooltip, true);
            window.removeEventListener("resize", hideTooltip);
            tooltip.remove();
        };
    }, [rootEl, getBoundsEl, titleSelector, aboveModal]);
}
